import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    EventEmitter,
    HostBinding,
    InjectionToken,
    Input,
    Output,
    QueryList
} from "@angular/core";
import {EditGridAddRowActionsComponent} from "./edit-grid-add-row-actions/edit-grid-add-row-actions.component";
import {
    EditGridAddRowNewSelectResults, EditGridAddRowQuickSelectResults,
    EditGridAddRowSearchSelectResults,
    EditGridAddRowService
} from "./edit-grid-add-row.service";
import {EntityData, LocalisableEntityData} from "@fedeltapointofsale/data-client";
import {EditGridService, WrappedBoolean, WrappedNumber, WrappedString} from "../edit-grid.service";
import {AddRowActionComponent} from "./edit-grid-add-row-actions/add-row-action.component";
import {EditGridAddRowQuickAddComponent} from "./edit-grid-add-rowquick-add/edit-grid-add-row-quick-add.component";

export const EDIT_GRID_ADD_ROW_CONTEXT = new InjectionToken<boolean>("EDIT_GRID_ADD_ROW_CONTEXT");

@Component({
    selector: "wbo-edit-grid-add-row",
    styleUrls: ["./edit-grid-add-row.component.scss"],
    templateUrl: "./edit-grid-add-row.component.html",
    providers: [EditGridAddRowService, {provide: EDIT_GRID_ADD_ROW_CONTEXT, useValue: true}],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditGridAddRowComponent implements AfterContentInit {
    @Input() public identifierField: string;
    @Input() public componentPath: string;
    @Input() public entityIdField: string;
    @Input() public assignLocalisation: boolean;
    @Input() public customAdd: boolean = false;
    @Input() public customAddSearchSelect: boolean = false;
    @Input() public customAddNewSelect: boolean = false;
    @Input() public customAddNew: boolean = false;
    @Input() public customAddQuickSelect: boolean = false;
    @Input() public validateRecordExists: boolean = true;
    @Input() public editOnAdd: boolean = false;
    @Input() public selectOnAdd: boolean = false;
    @Output() public onSearchSelect: EventEmitter<any[]>;
    @Output() public onNewSelect: EventEmitter<EntityData<any>>;
    @Output() public onNew: EventEmitter<EditGridAddRowComponent>;
    @Output() public onAfterEntityAdded: EventEmitter<EntityData<any>>;
    @ContentChild(EditGridAddRowActionsComponent) public editGridAddRowActionsComponent: EditGridAddRowActionsComponent;
    @ContentChild(EditGridAddRowQuickAddComponent) public editGridAddRowQuickAddComponent: EditGridAddRowQuickAddComponent;

    @HostBinding("class.active") public active: boolean;
    @HostBinding("class.with-content") public get withContent(): boolean {
        return !!this.editGridAddRowQuickAddComponent;
    }

    constructor(private editGridAddRowService: EditGridAddRowService, private editGridService: EditGridService) {
        this.customAdd = false;
        this.onSearchSelect = new EventEmitter<any[]>();
        this.onNewSelect = new EventEmitter<EntityData<any>>();
        this.onNew = new EventEmitter<any>();
        this.onAfterEntityAdded = new EventEmitter<EntityData<any>>();
        editGridAddRowService.onSearchSelect.subscribe((results) => this.addSearchSelect(results));
        editGridAddRowService.onNewSelect.subscribe((results) => this.addNewSelect(results));
        editGridAddRowService.onQuickSelect.subscribe((results) => this.addQuickSelect(results));
        editGridAddRowService.onNew.subscribe(() => this.addNew());
    }

    ngAfterContentInit(): void {
        if (this.editGridAddRowActionsComponent) {
            this.active = true;
        }
    }

    /**
     *
     * @returns {QueryList<AddRowActionComponent>}
     */
    get actions(): QueryList<AddRowActionComponent> {
        return this.editGridAddRowActionsComponent ? this.editGridAddRowActionsComponent.actions : null;
    }

    /**
     *
     * @param {EditGridAddRowSearchSelectResults} searchSelectResults
     */
    addSearchSelect(searchSelectResults: EditGridAddRowSearchSelectResults): void {
        let firstRow: boolean = true;
        if (this.customAdd || this.customAddSearchSelect) {
            this.onSearchSelect.next(searchSelectResults.data);
        } else {
            searchSelectResults.data.forEach((entity) => {
                if (this.addRow(entity, firstRow, this.validateRecordExists)) {
                    firstRow = false;
                }
            });
        }
    }

    /**
     *
     * @param {EditGridAddRowNewSelectResults} newSelectResults
     */
    addNewSelect(newSelectResults: EditGridAddRowNewSelectResults): void {
        if (this.customAdd || this.customAddNewSelect) {
            this.onNewSelect.next(newSelectResults.entity);
        } else {
            this.addRow(newSelectResults.entity, true);
        }
    }

    /**
     *
     * @param {EditGridAddRowQuickSelectResults} quickSelectResults
     */
    addQuickSelect(quickSelectResults: EditGridAddRowQuickSelectResults): void {
        if (this.customAdd || this.customAddQuickSelect) {
            this.onNewSelect.next(quickSelectResults.entity);
        } else {
            this.addRow(quickSelectResults.entity, true);
        }
    }

    addNew(): void {
        if (this.customAdd || this.customAddNew) {
            this.onNew.next(this);
        } else {
            this.addRow(this.editGridService.createEntity(), true, false);
        }
    }

    /**
     *
     * @param newEntity
     * @param {boolean} goToEditMode
     * @param checkExists
     * @returns {boolean}
     */
    addRow(newEntity: any, goToEditMode: boolean = false, checkExists: boolean = true): boolean {
        if (checkExists && this.exists(newEntity)) {
            return false;
        }
        let entityAdded: EntityData<any> | any = this.editGridService.isEntityData ? this.editGridService.createEntity() : newEntity;
        let size: number;

        if (this.editGridService.primitive) {
            entityAdded.value = newEntity.value;
            size = this.editGridService.addRow(entityAdded);
        } else if (this.editGridService.isEntityData) {
            size = this.editGridService.addRow(entityAdded);

            const methodName: string = "from" + newEntity.constructor.name.charAt(0).toUpperCase() + newEntity.constructor.name.slice(1);
            if (typeof entityAdded.constructor[methodName] === "function") {
                entityAdded = entityAdded.constructor[methodName](newEntity);
            } else {
                const entity: EntityData<any> = this.componentPath ? EntityData.getField(entityAdded, this.componentPath) : entityAdded;
                entity.assign(newEntity);
                if (this.entityIdField) {
                    entityAdded[this.entityIdField] = entity.getIdentifier();
                }
            }

            if (this.assignLocalisation && entityAdded instanceof LocalisableEntityData) {
                entityAdded.localisableContext.universal = true;
            }
        } else {
            size = this.editGridService.addRow(entityAdded);
        }

        this.onAfterEntityAdded.next(entityAdded);

        if (this.editGridService.editable && this.editOnAdd && goToEditMode) {
            setTimeout(() => this.editGridService.editRow(size - 1, entityAdded, true));
            if (this.editGridService.editGrid.selectable && !this.editGridService.editGrid.multiSelect) {
                this.editGridService.editGrid.selectedRowIndex = this.editGridService.editGrid.data.length - 1;
            }
        } else if (this.selectOnAdd && this.editGridService.editGrid.selectable && !this.editGridService.editGrid.multiSelect) {
            this.editGridService.editGrid.selectedRowIndex = this.editGridService.editGrid.data.length - 1;
            this.editGridService.editGrid.focusOnRowIndex(this.editGridService.editGrid.selectedRowIndex);
        }

        return true;
    }

    /**
     *
     * @param entity
     * @returns {boolean}
     */
    exists(entity: any): boolean {
        if (this.editGridService.primitive) {
            return this.editGridService.data.find((item: any) => ((item instanceof WrappedString || item instanceof WrappedNumber || item instanceof WrappedBoolean) ? item.value : item) === entity.value) !== undefined;
        } else {
            return this.editGridService.data.find((item) => {
                item = this.componentPath ? EntityData.getField(item, this.componentPath) : item;
                if (!this.identifierField && !(entity instanceof EntityData)) {
                    throw new Error("identifierField not defined");
                }
                const itemIdentifier: any = item instanceof EntityData ? item.getIdentifier() : item[this.identifierField];
                return itemIdentifier === (this.identifierField ? entity[this.identifierField] : entity.getIdentifier());
            }) !== undefined;
        }
    }
}
